import React, { createContext, useContext, useState, useEffect } from 'react';
import { channelPartnerAuthAPI } from '../services/api';

// Mirrors contexts/SuperAdminContext.js's shape, for the Channel Partner's own portal.
const ChannelPartnerAuthContext = createContext(null);

const PARTNER_TOKEN_KEY = 'partner_auth_token';
const PARTNER_USER_KEY = 'partner_user';

export const ChannelPartnerAuthProvider = ({ children }) => {
  const [partner, setPartner] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(PARTNER_TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await channelPartnerAuthAPI.me();
      if (response.data.success) {
        setPartner(response.data.data);
        localStorage.setItem(PARTNER_USER_KEY, JSON.stringify(response.data.data));
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching channel partner profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await channelPartnerAuthAPI.login(email, password);
      if (response.data.success) {
        const newToken = response.data.data.token;
        const partnerData = response.data.data.partner;

        setToken(newToken);
        setPartner(partnerData);
        localStorage.setItem(PARTNER_TOKEN_KEY, newToken);
        localStorage.setItem(PARTNER_USER_KEY, JSON.stringify(partnerData));

        return { success: true, partner: partnerData };
      }
      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setPartner(null);
    localStorage.removeItem(PARTNER_TOKEN_KEY);
    localStorage.removeItem(PARTNER_USER_KEY);
  };

  const value = {
    partner,
    token,
    loading,
    isAuthenticated: !!token && !!partner,
    login,
    logout,
    refreshProfile: fetchProfile,
  };

  return (
    <ChannelPartnerAuthContext.Provider value={value}>
      {children}
    </ChannelPartnerAuthContext.Provider>
  );
};

export const useChannelPartnerAuth = () => {
  const context = useContext(ChannelPartnerAuthContext);
  if (!context) {
    throw new Error('useChannelPartnerAuth must be used within a ChannelPartnerAuthProvider');
  }
  return context;
};

export default ChannelPartnerAuthContext;
